import React, { PureComponent } from "react";
import moment from "moment";
import Uri from "jsuri";
import AbortController from "abort-controller";

import { component } from "./component";
import Loader from "./Loader/Loader";
import WidgetHeader from "./WidgetHeader";
import { allowedWidgetWithoutURL, BASEURL, HEADERS } from "./Utils/Constants";

import * as Sentry from "@sentry/react";
import { areArraysDifferent, Error } from "./Utils/Utils.js";

const absolute = (uri) => {
  return uri.includes("http") ? uri : `${BASEURL}${uri}`;
};

class WidgetContainer extends PureComponent {
  constructor(props) {
    super(props);

    this.widgetController = new AbortController();
    this.mounted = false;

    this.state = {
      data: null,
      date_ranges: null,
      url: props.url,
      loading_content: true,
      loading_header: true,
      has_error: false,
      error_message: "",

      items: [],
      selectedItem: [],
      start_date: this.props.start_date,
      end_date: this.props.end_date,

      group_slug: props.group_slug,
      region_slug: props.group_slug,
      boardFilters: props.boardFilters,

      childTableRef: null,

      defaultDateRange: new Uri(this.props.url).getQueryParamValue("t"),
    };

    this.childRef = React.createRef();
  }

  async fetchData(newUrl) {
    const TOKEN = window.localStorage.token;
    this.widgetController.abort();
    this.widgetController = new AbortController();
    let signal = this.widgetController.signal;
    this.setState({
      url: newUrl,
      loading_content: true,
    });

    // checks whether there's a threshold prop from the browser URL
    const thresholdParam = new Uri(window.location.search).getQueryParamValue(
      "threshold"
    );

    // if so then replace it by the one from the url
    if (thresholdParam) {
      let urlData = new Uri(newUrl);
      urlData = urlData.replaceQueryParam("threshold", thresholdParam);
      newUrl = urlData.toString();
    }

    try {
      const response = await fetch(absolute(newUrl), {
        method: "GET",
        headers: HEADERS(TOKEN),
        signal: signal,
      });
      if (!response.ok) {
        if (this.mounted) {
          this.setState({
            error_message: response.statusText,
            has_error: true,
          });
        }

        throw new Error(response.statusText);
      }

      const data = await response.json();

      if (this.mounted) {
        this.setState({
          data: data,
          date_ranges:
            data && data.meta && data.meta.dates ? data.meta.dates : null,
          loading_content: false,
          loading_header: false,

          error_message: data.message ? data.message : "",
          has_error: data.error ? data.error : false,
        });
      }
    } catch (error) {
      if (this.mounted) {
        this.setState({
          error_message: error.message,
          has_error: true,
        });
      }
    }
  }

  isDisplayOnlyWidget = () => {
    const w = this.props.widget;
    return (
      w === "PageHeader" ||
      w === "PageDivider" ||
      allowedWidgetWithoutURL.includes(w)
    );
  };

  getElementFromChild = (childRef) => {
    this.setState({
      childTableRef: childRef,
    });
  };

  componentDidMount() {
    // Differentiate between presentational and data widgets
    if (!this.isDisplayOnlyWidget()) {
      this.fetchData(this.props.url);
    }
  }

  UNSAFE_componentWillMount() {
    this.mounted = true;
    this.setState({
      start_date: undefined,
      end_date: undefined,
    });
    this.props.pageContainerGlobalDateChange(undefined, undefined);
  }
  componentWillUnmount() {
    this.mounted = false;
    this.widgetController.abort();
  }

  getDatePresetName = (uri) => {
    const { data, date_ranges } = this.state;

    if (data !== null && date_ranges) {
      return date_ranges.find((obj) => obj.slug === uri.getQueryParamValue("t"))
        .name;
    }
  };

  getDatePresetStartEndDates = (slug, start_end) => {
    const { date_ranges } = this.state;

    if (date_ranges !== null && slug) {
      const fromto = date_ranges.find((o) => o.slug === slug)[start_end];
      return fromto;
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.url !== prevProps.url ||
      this.props.start_date !== prevProps.start_date ||
      this.props.end_date !== prevProps.end_date ||
      this.props.group_slug !== prevProps.group_slug ||
      this.props.region_slug !== prevProps.region_slug ||
      areArraysDifferent(
        this.props.boardFilters || [],
        prevProps.boardFilters || []
      ) ||
      this.props.page !== prevProps.page
    ) {
      if (!this.isDisplayOnlyWidget()) {
        let url = this.props.url;
        let baseUrl = new Uri(url);
        let group_slug = this.props.group_slug;
        let boardFilters = this.props.boardFilters;
        let region_slug = this.props.region_slug;

        let has_groups = this.props.has_groups !== null ? true : false;
        let has_regions = this.props.has_regions !== null ? true : false;
        let has_filter = this.props.has_filter !== null ? true : false;

        let start_date =
          this.props.start_date || baseUrl.getQueryParamValue("start");
        let end_date = this.props.end_date || baseUrl.getQueryParamValue("end");

        this.setState({
          selectedItem: new Uri(this.props.url).getQueryParamValue("t"),
          start_date: start_date,
          end_date: end_date,
        });

        // Date Ranges
        if (start_date && end_date) {
          url = baseUrl.deleteQueryParam("t");
          url = baseUrl
            .deleteQueryParam("start")
            .addQueryParam("start", start_date);
          url = baseUrl.deleteQueryParam("end").addQueryParam("end", end_date);
        }

        // Groups & Regions
        if (has_groups || has_regions || has_filter) {
          if (group_slug !== null) {
            url = baseUrl.deleteQueryParam("g").addQueryParam("g", group_slug);
          }

          if (region_slug !== null) {
            url = baseUrl.deleteQueryParam("r").addQueryParam("r", region_slug);
          }

          if (Array.isArray(boardFilters) && boardFilters.length > 0) {
            const payloadObject = boardFilters.reduce((acc, f) => {
              if (!acc[f.dimensionId]) {
                acc[f.dimensionId] = { values: [] };
              }
              acc[f.dimensionId].values.push(f.name);
              return acc;
            }, {});

            const payloadFilters = Object.entries(payloadObject).map(
              ([dimension, { values }]) => ({ dimension, values })
            );

            url = baseUrl
              .deleteQueryParam("filters")
              .addQueryParam("filters", JSON.stringify(payloadFilters));
          }

          // Groups and regions start with defaults
          if (
            this.props.group_slug !== prevProps.group_slug ||
            this.props.region_slug !== prevProps.region_slug ||
            this.props.filter_id !== prevProps.filter_id
          ) {
            if (start_date && end_date) {
              url = baseUrl
                .replaceQueryParam("start", start_date)
                .replaceQueryParam("end", end_date);
            } else {
              url = baseUrl
                .deleteQueryParam("t")
                .deleteQueryParam("start")
                .deleteQueryParam("end")
                .addQueryParam("t", this.state.defaultDateRange);
            }
          }
        }
        this.fetchData(decodeURI(url));
      }
    }
  }

  handleCustomDateRangeChange = (start, end) => {
    const { url } = this.state;

    const start_date = moment(start).format("MM/DD/YY");
    const end_date = moment(end).format("MM/DD/YY");

    this.setState({
      selectedItem: `${start_date + " - " + end_date}`,
    });

    let newUrl = decodeURI(
      new Uri(url)
        .deleteQueryParam("t")

        .deleteQueryParam("start")
        .deleteQueryParam("end")
        .addQueryParam("start", start)
        .addQueryParam("end", end)
    );

    this.fetchData(newUrl);

    window.analytics.track("Widget Date Change", {
      type: "Custom Date Change",
      start_date: start_date,
      end_date: end_date,
      url: url,
      widget: this.props.widget,
      widget_title: this.props.title,
    });
  };

  widgetContainerGlobalDateChange = (start, end) => {
    const start_date = moment(start).format("MM/DD/YY");
    const end_date = moment(end).format("MM/DD/YY");

    this.setState({
      start_date: start_date,
      end_date: end_date,
    });
    this.props.pageContainerGlobalDateChange(start, end);
  };

  render() {
    const {
      title,
      widget,
      size,
      description,
      project,
      project_id,
      has_datepicker,
      page_title,
      csv_export,
      first_tick,
      subtitle,
      tooltip,
      insightsConfig,
      insights,
      goal,
      chart,
      table_filter,
      cells,
      columns,
      metrics,
      funnels,
      group_name,
      pagination,
      footer,
      test_selector,
      maintenance,
    } = this.props;

    const {
      url,

      data,
      date_ranges,
      loading_content,
      loading_header,
      has_error,
      error_message,
      selectedItem,
    } = this.state;

    let col_size = "";

    switch (size) {
      case "lg":
        col_size = "col-12 d-flex mb-3";
        break;
      case "md":
        col_size = "col-sm-12 col-md-6 d-flex mb-5";
        break;
      case "sm":
        col_size = "col-sm-12 col-md-4 d-flex mb-5";
        break;
      case "xs":
        col_size = "col-sm-12 col-md-3 d-flex mb-5";
        break;
      default:
        col_size = "col-sm-12 col-md-6 d-flex mb-5";
        break;
    }
    const comingSoonClass = maintenance ? "widget--coming-soon" : "";
    const firstTickClass = first_tick ? "widget--show-first-tick" : "";
    const widget_class =
      `widget ${firstTickClass} ${test_selector} ${comingSoonClass}`.trim();

    let selected_date = "";
    let start = "";
    let end = "";
    const current_uri = new Uri(url);

    if (has_datepicker) {
      // If we have `t` param, we load from date presets
      if (current_uri.hasQueryParam("t")) {
        if (selectedItem && selectedItem.length === 0 && has_datepicker) {
          // Friendly name
          selected_date = this.getDatePresetName(current_uri);
        } else {
          selected_date = selectedItem;
        }

        let slug = current_uri.getQueryParamValue("t");
        start = this.getDatePresetStartEndDates(slug, "from");
        end = this.getDatePresetStartEndDates(slug, "to");
      } else {
        current_uri.deleteQueryParam("t");
        start = current_uri.getQueryParamValue("start");
        end = current_uri.getQueryParamValue("end");

        selected_date =
          moment(start).format("MM/DD/YY") +
          " - " +
          moment(end).format("MM/DD/YY");
      }
    }

    const Component = component(widget);

    const heights = {
      // FLM
      Visitors: 218,
      PageViews: 360,
      PageEngagement: 360,
      WhereTheyViewed: 360,
      VisitsBySource: 390,
      TopReferrers: 700,
      NewReturning: 525,
      ReturnFrequency: 525,
      ViewsYesterday: 495,
      MinutesWatchedYesterday: 495,
      VideoViewsByNetwork: 290,

      SocialFollowersSubscribers: 380,
      FollowersSubscribersBreakdown: 380,
      Facebook: 452,
      YouTube: 410,

      // Britbox Summary
      SummaryFreeTrials: 428,
      SummaryPaidSubs: 428,
      Financial: 105,

      // Britbox Watching
      WatchingSummary: 203,
      PieChartLegends: 375,

      // Britbox Subscribers
      SubscribersSummary: 100,
      FreeTrialConversion: 236,
      SubscriberRenewalCancel: 236,
      FreeTrialStarts: 440,
      DailyActives: 440,

      // Britbox Board Report
      LastWeekSummary: 123,
      BarChartLegendsStackedBoardReport: 412,
      LastWeekViewing: 90,
      BarChartLegendsStacked: 412,
      BarChartLegendsStackedYourBiz: 412,
      Top20Content: 1805,

      // Snapchat
      SnapChatAggregatedSummary: 690,
      TopSeriesByUniqueViews: 380,
      TopSeriesByUniqueViewers: 380,

      // Powerphyl
      AdsCoreMetrics: 204,
      PrimeSeriesSocialTopContent: 400,
      PrimeSeriesSocialHashtagsSummary: 400,

      LeeChurn: 200,
      LeeTotalStarts: 200,
    };

    // allow to display the WidgetHeader for those widgets that contains the URL and wants to display the title (and other props, for example)
    // some widgets may be displayed without containing the URL
    if (url || allowedWidgetWithoutURL.includes(widget)) {
      return (
        <Sentry.ErrorBoundary>
          <div className={col_size}>
            <div className="widget--coming-soon-info widget__title">
              Coming Soon
            </div>
            <section className={widget_class}>
              {(!loading_header ||
                // display the header for those widgets that doesn't contain URL
                allowedWidgetWithoutURL.includes(widget)) && (
                <WidgetHeader
                  URL={url}
                  TITLE={title}
                  DESCRIPTION={description}
                  PAGE_TITLE={page_title}
                  SUBTITLE={subtitle}
                  HAS_DATEPICKER={has_datepicker}
                  TOOLTIP={tooltip}
                  CSV_EXPORT={csv_export}
                  TABLE_FILTER={table_filter}
                  SELECTED_DATE={selected_date}
                  DATE_RANGES={date_ranges}
                  START_DATE={start}
                  END_DATE={end}
                  component={() => this.childRef}
                  handleCustomDateRangeChange={this.handleCustomDateRangeChange}
                  widgetContainerGlobalDateChange={
                    this.widgetContainerGlobalDateChange
                  }
                />
              )}
              {/* don't display the loader for those widget that shouldn't load any data */}
              {loading_content && !allowedWidgetWithoutURL.includes(widget) ? (
                <Loader
                  minHeight={heights[widget] || 400}
                  col={"col-12"}
                  size="circle-scale--sm"
                />
              ) : // don't show data message error for those widget disconnected from the data/endpoint
              has_error && !allowedWidgetWithoutURL.includes(widget) ? (
                <Error
                  message={error_message}
                  minHeight={360}
                  report={() => Sentry.showReportDialog()}
                  {...this.props}
                />
              ) : (
                <Component
                  widget={widget}
                  data={data}
                  title={title}
                  url={url}
                  project={project}
                  project_id={project_id}
                  goal={goal}
                  cells={cells}
                  chart={chart}
                  insightsConfig={insightsConfig}
                  columns={columns}
                  metrics={metrics}
                  funnels={funnels}
                  pagination={pagination}
                  footer={footer}
                  group_name={group_name}
                  childRef={(el) => (this.childRef = el)}
                  passRefUpward={this.getElementFromChild}
                  insights={insights}
                  table_filter={table_filter}
                />
              )}
            </section>
          </div>
        </Sentry.ErrorBoundary>
      );
    } else {
      return (
        <Sentry.ErrorBoundary>
          <Component title={title} subtitle={subtitle} tooltip={tooltip} />
        </Sentry.ErrorBoundary>
      );
    }
  }
}

export default WidgetContainer;
